<script>
  import { createEventDispatcher, onMount, getContext } from 'svelte';
  import { getGroupStore } from '@stores/group';
  let groupStore = getGroupStore('groupStore');
  let recObjectsStore = getGroupStore('recObjectsStore');
  let submittedCommentStore = getGroupStore('submittedCommentStore');

  import Notification from '@utils/notification';
  import BrowserUtils from '@utils/browser-utils';

  import CommentAjax from '@ajax/comment';
  import GroupAjax from '@ajax/group';
  import RecObjectAjax from '@ajax/rec_object';

  import CommentList from './CommentList.svelte';
  import { getCurrentUserStore } from '@stores/current_user';

  const dispatch = createEventDispatcher();

  export let recObjectId = null;
  export let mobile = false;
  export let viewStyle = 'list';
  export let invertedColors = false;
  export let customGroupStore;
  export let customContext;
  export let el;
  let recObject = recObjectId ? recObjectsStore.find(recObjectId) : null;
  let group = customGroupStore || groupStore;
  let commentOwner = recObject || group;
  let currentUser = getCurrentUserStore('default');
  let comments = { total: 0, data: [] };

  let reportedContent = getContext('reportedContent');

  let visibleComments = { total: 0, data: [] };
  let hiddenComments = { total: 0, data: [] };
  let hasHiddenComments = false;
  let showHiddenComments = false;

  $: {
    if ($reportedContent) {
      hasHiddenComments = Object.keys($reportedContent).some((key) => key.startsWith('comment_'));
    }
    if (comments && comments.data) {
      hasHiddenComments = hasHiddenComments || comments.data.some((comment) => comment.flagged);

      if (hasHiddenComments) {
        const [visible, hidden] = comments.data.reduce(
          ([visible, hidden], comment) => {
            if ($reportedContent[`comment_${comment.id}`] !== undefined || comment.flagged) {
              hidden.push(comment);
            } else {
              visible.push(comment);
            }
            return [visible, hidden];
          },
          [[], []]
        );

        visibleComments = { total: visible.length, data: visible };
        hiddenComments = { total: hidden.length, data: hidden };
      } else {
        visibleComments = { total: comments.data.length, data: comments.data };
        hiddenComments = { total: 0, data: [] };
      }
    }
  }

  $: expandedComments = commentOwner.comments
    ? commentOwner.comments.data
        .filter((c) => c.replies.filter((reply) => !reply.flagged).length > 0)
        .map((c) => {
          return c.id;
        })
    : [];

  currentUser.subscribe((x) => (currentUser = x));

  $: showComments = commentOwner.comments && commentOwner.comments.data.length > 0;

  let deleteComment = async function (comment, spamChecked) {
    await CommentAjax.delete(group, comment, spamChecked);
    await reFetchRecObject();

    dispatch('changed', { action: 'delete', id: comment.id });

    let message = 'Comment deleted';

    if (spamChecked) {
      message += ' and reported as spam';
    }

    Notification.send({
      text: message,
    });
  };

  let reFetchRecObject = async function () {
    if (recObjectId) {
      recObject.set(await RecObjectAjax.get(group, recObject));
    } else {
      groupStore.set(
        await GroupAjax.get(group.id, {
          withErrorHandling: false,
        })
      );
    }
  };

  const toggleReplies = function (comment) {
    if (expandedComments.includes(comment.id)) {
      hideReplies(comment);
    } else {
      showReplies(comment, false);
    }
  };

  const showReplies = function (replyComment) {
    let comment = replyComment;

    if (replyComment.parent) {
      comment = replyComment.parent;
    }

    if (!expandedComments.includes(comment.id)) {
      expandedComments = [...expandedComments, comment.id];
    }
  };

  const hideReplies = function (comment) {
    expandedComments = expandedComments.filter((item) => item != comment.id);
  };

  const highlightReply = async function (comment) {
    // jQuery(`#${comment.id}-mobile-grid-focus-input`).focus();
    setTimeout(async () => {
      const elem = document.getElementById(`comment-box-${comment.id}`);

      if (!elem) {
        return;
      }

      if (mobile) {
        await BrowserUtils.goToElement(elem, { behavior: 'smooth', block: 'center', inline: 'center' });
      } else {
        let container =
          document.querySelector('.detail-scrollable-notes-comments') ||
          document.querySelector('.comments-listing-container');

        if (!container) {
          return;
        }

        await BrowserUtils.goToElementWithinContainer(elem, container, {
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
      }

      await BrowserUtils.focusAndHighlight(elem);
    }, 0);
  };

  let unsubRecObject = commentOwner.subscribe((x) => {
    commentOwner = x;
    const newComments = commentOwner.comments || { total: 0, data: [] };
    comments = newComments;
  });

  let unsubSubmittedComment = submittedCommentStore.subscribe((x) => {
    let comment = x.comment;
    if (comment) {
      let parentId = x.parentId;
      if (parentId && expandedComments && !expandedComments.includes(parentId)) {
        expandedComments = [...expandedComments, parentId];
      }
      highlightReply(comment);
    }
  });

  onMount(async () => {
    return () => {
      unsubRecObject && unsubRecObject();
      unsubSubmittedComment && unsubSubmittedComment();
    };
  });
</script>

<style style="text/scss" lang="scss">
  @import '../../vendor/design-system.scss';

  * {
    --editable-focused-border-color: transparent;
  }
  .editable-content-wrapper {
    border-radius: 8px !important;
  }

  .comments-box {
    background: #f8f6ff;
    border-radius: 15px;
    padding: 30px;
    padding-top: 35px;
    font-size: 13px;
    cursor: auto;
    margin-top: -20px;
  }

  .invertedColors {
    background: white !important;
  }

  // dynamically used from viewStyle
  .grid {
    width: 380px !important;
    display: flex !important;
    flex-direction: column-reverse !important;

    .view-more-and-add-button {
      margin-top: 10px !important;
      margin-bottom: 5px !important;
    }

    .add-comment-msg {
      color: #8e71ff !important;

      &:hover {
        color: #7054dc !important;
      }
    }
  }

  .grid.mobile {
    width: 100% !important;
  }

  // dynamically used from viewStyle
  .detail-view {
    margin-top: 0px !important;
    background: white !important;
    padding: 0px !important;

    .comment-body {
      background: #f5f3fc !important;
    }
  }

  .post-comment-text {
    align-self: flex-end;
    cursor: pointer;
    &.desktop {
      padding-bottom: 3px;
    }
  }

  * :global {
    .spam-header {
      font-weight: 500;
      font-size: 16px;
    }
    .spam-alert-text {
      font-weight: 400;
      font-size: 14px;
    }
  }

  .hidden-comments-box {
    margin-top: 20px;
    border-top: 1px solid #ececec;
    padding-top: 20px;
  }

  .hidden-comment-text {
    display: flex;
    align-items: center; // Vertically center the items
    cursor: pointer;
    padding: 16px 0px;

    .icon {
      margin-right: 20px; // Add some space between the icon and text
    }

    p {
      margin: 0; // Remove default paragraph margin
      font-size: 14px;
      font-weight: 500;
      color: $neutral-gray;
    }
  }
</style>

{#key comments}
  {#if showComments != false && (visibleComments.total > 0 || hiddenComments.total > 0)}
    <div
      bind:this={el}
      class="comments-box undraggable {viewStyle}"
      class:invertedColors={invertedColors}
      class:mobile={mobile}>
      {#if visibleComments.total > 0}
        <CommentList
          comments={visibleComments.data}
          expandedComments={expandedComments}
          currentUser={currentUser}
          group={group}
          recObject={recObject}
          invertedColors={invertedColors}
          mobile={mobile}
          viewStyle={viewStyle}
          customContext={customContext}
          deleteComment={deleteComment}
          reFetchRecObject={reFetchRecObject}
          toggleReplies={toggleReplies} />
      {/if}

      {#if hiddenComments.total > 0}
        <div
          class="hidden-comment-text"
          on:click={() => {
            showHiddenComments = !showHiddenComments;
            dispatch('commentsHidden');
          }}
          class:hidden={showHiddenComments}>
          <img class="icon" style="color: gray" src="/images/report/hidden-eye.svg" alt="hide-icon" />
          <p>View hidden comments</p>
        </div>

        {#if showHiddenComments}
          <CommentList
            comments={hiddenComments.data}
            expandedComments={expandedComments}
            currentUser={currentUser}
            group={group}
            recObject={recObject}
            invertedColors={invertedColors}
            mobile={mobile}
            viewStyle={viewStyle}
            customContext={customContext}
            deleteComment={deleteComment}
            reFetchRecObject={reFetchRecObject}
            toggleReplies={toggleReplies} />
        {/if}
      {/if}

      <div class="end-of-comments"></div>
    </div>
  {/if}
{/key}
